<template>
  <v-container class="modern-view pb-12">
    <ValidationObserver v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(save)">
        <!-- Premium Header -->
        <v-row class="mb-8 align-center px-4 pt-4">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white">fas fa-user-shield</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-black primary--text lh-1">
                {{ id > 0 ? 'Actualizar' : 'Nuevo' }} <span class="secondary--text">Rol</span>
              </div>
              <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
                Definición de perfiles y niveles de acceso al sistema
              </div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <v-btn @click="$router.push('/users')" text class="grey--text font-weight-bold mr-4 rounded-lg">
              <v-icon left small>fas fa-chevron-left</v-icon> Volver
            </v-btn>
            <v-btn type="submit" color="primary" class="shadow-sm rounded-lg px-6 font-weight-black" :loading="loading">
              <v-icon left small>fas fa-save</v-icon> Guardar Rol
            </v-btn>
          </div>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <v-card outlined class="modern-card pa-8 shadow-sm">
              <!-- Role Name Section -->
              <div class="d-flex align-center mb-6">
                <v-avatar size="32" color="primary lighten-5" class="mr-3">
                  <v-icon color="primary" small>fas fa-id-badge</v-icon>
                </v-avatar>
                <span class="text-h6 font-weight-black primary--text">Identificación del Perfil</span>
              </div>

              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Nombre del Rol</div>
                <v-text-field
                  v-model='name'
                  outlined dense
                  prepend-inner-icon="fas fa-sliders-h"
                  :error-messages="errors"
                  class="modern-input"
                  placeholder="Ej: Supervisor de Producción"
                ></v-text-field>
              </ValidationProvider>

              <v-divider class="my-8"></v-divider>

              <!-- Permissions Section -->
              <div class="d-flex align-center mb-6">
                <v-avatar size="32" color="secondary lighten-5" class="mr-3">
                  <v-icon color="secondary" small>fas fa-key</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6 font-weight-black secondary--text lh-1">Matriz de Permisos</div>
                  <div class="caption grey--text font-weight-bold">Seleccione las funcionalidades habilitadas para este rol</div>
                </div>
              </div>

              <v-sheet outlined class="pa-2 rounded-lg bg-lightgrey border-dashed">
                <v-select
                  v-model="permits"
                  :items="access_list"
                  item-text="name"
                  label="Asignar Permisos"
                  return-object
                  multiple
                  outlined
                  flat
                  solo
                  background-color="transparent"
                  hide-details
                  class="modern-permissions-select"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip 
                      v-if="index < 10" 
                      x-small 
                      label 
                      dark 
                      color="secondary" 
                      class="font-weight-black ma-1 shadow-xs"
                    >
                      {{ item.name }}
                    </v-chip>
                    <span v-if="index === 10" class="grey--text caption font-weight-black ml-2 mt-1">
                      (+{{ permits.length - 10 }} más)
                    </span>
                  </template>
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-on="on" v-bind="attrs" dense class="px-2">
                       <v-list-item-action class="mr-4 my-1">
                         <v-checkbox :input-value="attrs.inputValue" color="secondary" hide-details dense></v-checkbox>
                       </v-list-item-action>
                       <v-list-item-content>
                         <v-list-item-title class="body-2 font-weight-bold grey--text text--darken-3">{{ item.name }}</v-list-item-title>
                       </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-sheet>
              
              <div class="mt-4 px-2 d-flex align-center">
                <v-icon x-small color="grey lighten-1" class="mr-2">fas fa-info-circle</v-icon>
                <span class="caption grey--text italic">Los cambios en los permisos afectarán a todos los usuarios vinculados a este rol.</span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import notifications from '@/utils/notifications'
import { getRole, createRole, updateRole } from '@/api/auth'
import access_list from '@/utils/access_list'

export default {
  name: 'RoleCreateUpdate',
  data() {
    return {
      loading: false,
      access_list: access_list,
      id: '',
      name: '',
      permits: [],
    }
  },
  methods: {
    async save() {
      this.loading = true
      try {
        if (this.id > 0) {
          const role = await updateRole({
            id: this.id,
            name: this.name,
            permits: this.permits,
          })
          notifications.showSuccess(`Rol ${role.name} Actualizado`)
        } else {
          const role = await createRole({
            name: this.name,
            permits: this.permits,
            access: []
          })
          notifications.showSuccess(`Rol ${role.name} Creado`)
        }
        this.$router.push({ name: 'Users' })
      } catch (e) {
        notifications.showError('Error al procesar el rol: ' + e)
      } finally {
        this.loading = false
      }
    }
  },
  async created() {
    this.loading = true
    try {
      if (this.$route.params.id > 0) {
        const res = await getRole(this.$route.params.id)
        this.id = res.id
        this.name = res.name
        this.permits = res.permits
      } else {
        this.id = -1
      }
    } catch (e) {
      notifications.showError('Error de conexión ' + e)
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}

.bg-lightgrey { background-color: #f8fbfc !important; }
.border-dashed { border: 1px dashed #cfd8dc !important; }
.uppercase { text-transform: uppercase; letter-spacing: 1px; }
.tracking-widest { letter-spacing: 2px; }
.lh-1 { line-height: 1 !important; }
.italic { font-style: italic; }

.modern-permissions-select >>> .v-input__control >>> .v-input__slot {
  background-color: transparent !important;
}
</style>
